import "@/utils/polyfills";

import { createApp } from "vue";
import App from "@/App.vue";
import vuetify from "@/plugins/vuetify";

/**
 * Import styles after Vuetify imports "vuetify/styles".
 * This ensures that any overrides are handled correctly.
 */
import "@/assets/style/index.css";
import "@esenda/core-ui/core-ui.css";

import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";

import router from "@/plugins/router";
import i18n from "@/plugins/vi18n";
import { Amplify } from "aws-amplify";
import { CookieStorage } from "aws-amplify/utils";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import {
    init,
    replayIntegration,
    browserTracingIntegration,
    vueIntegration,
} from "@sentry/vue";
import { createSentryBreadcrumb, configSentryError } from "@/helpers/sentry";
import { useAlert } from "@esenda/core-ui/composables";

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const app = createApp(App);
const { showAlert } = useAlert();

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolClientId: import.meta.env.VITE_COGNITO_PAYMENT_CLIENT_ID,
            userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
            loginWith: {
                email: true,
                oauth: {
                    domain: import.meta.env.VITE_COGNITO_DOMAIN,
                    scopes: ["email", "openid"],
                    redirectSignIn: [`${window.origin}/auth/redirect`],
                    redirectSignOut: [`${window.origin}/auth/sign-out`],
                    responseType: "token",
                    providers: ["Google"],
                },
            },
            userAttributes: {
                email: { required: true },
            },
        },
    },
});

/** Store the token&user info in cookie storage*/
cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage());

/** Initialise Sentry */
init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    release: `${import.meta.env.VITE_APP_NAME}@${
        import.meta.env.VITE_APP_VERSION
    }`,
    tracePropagationTargets: [import.meta.env.VITE_API_URL, /^\//],
    integrations: [
        browserTracingIntegration({
            router,
            routeLabel: "name",
        }),
        replayIntegration({
            // masks PI from inputs/text on session replays
            // can be altered to allow only certain inputs to be hidden
            // more on https://docs.sentry.io/platforms/javascript/guides/vue/session-replay/privacy/
            maskAllText: true,
            // items in the dom with the following class will show up in the session replay
            unmask: [".sentry-unmask, [data-sentry-unmask]"],
            // Capture request information on in the network tab
            networkDetailAllowUrls: [],
            networkDetailDenyUrls: [],
            networkCaptureBodies: true,
        }),
        vueIntegration({
            tracingOptions: {
                trackComponents: false, // this needs to be off until this issue is fixed https://github.com/getsentry/sentry-javascript/issues/8143
            },
        }),
    ],
    debug: import.meta.env.VITE_ENVIRONMENT !== "production",
    tracesSampler: ({ name }) => {
        // Do not sample health checks ever
        if (
            name.includes("LloydsChallengeIframe") ||
            name.includes("LloydsMethodFormIframe")
        ) {
            // Drop this completelty, by setting its sample rate to 0%
            return 1;
        }

        // Else, use default sample rate
        return 0;
    },
    // Sentry v11 collects far more by default; keep the restrictive v10 baseline.
    dataCollection: {
        userInfo: true,
        cookies: false,
        httpHeaders: {
            request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
            response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
        },
        httpBodies: [],
        urlQueryParams: {
            deny: ["forwarded", "-ip", "remote-", "via", "-user"],
        },
        genAI: { inputs: false, outputs: false },
        databaseQueryData: false,
        queues: false,
        graphQL: { document: false, variables: false },
    },
    attachProps: true,
    attachStacktrace: true,
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_ENVIRONMENT,
    enabled: import.meta.env.VITE_ENVIRONMENT !== "local",
    /** List taken from Sentry's gist https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry */
    ignoreErrors: [
        "Non-Error promise rejection captured",
        // Random plugins/extensions
        "top.GLOBALS",
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        "originalCreateNotification",
        "canvas.contentDocument",
        "MyApp_RemoveAllHighlights",
        "http://tt.epicplay.com",
        "Can't find variable: ZiteReader",
        "jigsaw is not defined",
        "ComboSearch is not defined",
        "http://loading.retry.widdit.com/",
        "atomicFindClose",
        // Facebook borked
        "fb_xd_fragment",
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
        // reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268
        "bmi_SafeAddOnload",
        "EBCallBackMessageReceived",
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        "conduitPage",
        // Network errors such as going offline or being blocked by a proxy
        "Failed to fetch",
        /**
         * error triggered by IP addresses from Microsoft Corporation. This seems to be the issue:
         * "antifingerprint not defined yet. will try and handle event after its ready."
         * No solution/cause yet, but threads recommend to filter out the error:
         * https://github.com/getsentry/sentry-javascript/issues/9143
         * 688 events triggered in Sentry for 688 distinct users. The ip addresses point to Microsoft Corporation
         * */
        "TypeError: Illegal invocation",
        "TypeError: cancelled", // See https://stackoverflow.com/a/70452078
        // Sentry error triggered when RUM cannot be collected
        "TypeError ?(rum)",
        "(rum)",
        // Chunk/module load failures — handled and retried by the router.onError()
        // handler and the vite:preloadError handler. These are the raw browser
        // messages that surface before our handlers run; suppress raw Sentry events.
        "Importing a module script failed",
        "Failed to fetch dynamically imported module",
        "error loading dynamically imported module",
        /text\/html.*is not a valid JavaScript MIME type/,
        // Vue Router's secondary error when a chunk load failure causes the component
        // factory to return undefined. Always a consequence of the errors above;
        // suppressed here since the underlying cause is already handled.
        /Couldn't resolve component/,
        // CSS chunk preload failures after deployment (stale cache).
        // These are already captured as breadcrumbs via the vite:preloadError handler.
        /Unable to preload CSS/,
        /^performance\.getEntriesByType is not a function\. \(In 'performance\.getEntriesByType\("navigation"\)', 'performance\.getEntriesByType' is undefined\)$/,
        /^s\.getEntriesByType is not a function\. \(In 's\.getEntriesByType\(r\)', 's\.getEntriesByType' is undefined\)$/,

        // Snapchat in-app browser errors
        /SCDynimacBridge/i,

        // browser extension errors
        /safariextension/i,
        /Honey\.safariextension/i,

        // DuckDuckGo rejection by tracker-blocking/privacy-shield reporting
        /WKWebView API client did not respond to this postMessage/i,

        // Injected by in-app browsers / OS webviews that call a theme-change hook we don't define
        /onSystemThemeChanged is not defined/,

        // WeChat in-app browser (iOS) bridge script failing on its own handlers, not our code
        /weixinPostMessageHandlers/,
    ],
    denyUrls: [
        // Safari webkit
        /.*webkit-masked-url.*/,
        /^webkit-masked-url:\/\/hidden\/$/,
        // Legacy Safari App Extensions (e.g. .../Honey.safariextension/h0.js)
        /\.safariextension\//i,
        // Newer Safari Web Extensions, if you see these too
        /^safari-web-extension:\/\//i,
        /^safari-extension:\/\//i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Other plugins
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        // Firefox extensions
        /^resource:\/\//i,
    ],
    // maximum number of characters a message can have
    // before it will be truncated
    maxValueLength: 1000,
});

/**
 * Ensure Vite preload (module chunk import) failures are recorded.
 * Registered BEFORE app.mount() so it is active during the initial
 * module evaluation phase, when early CSS/JS chunk failures can occur.
 */
window.addEventListener("vite:preloadError", (event) => {
    // Always prevent Vite re-throwing the error — without this, Sentry captures
    // it as a full error event even though we handle it ourselves below.
    event.preventDefault();

    const { message } = event.payload;

    // Attempt a one-shot reload to pick up the latest assets after a deployment.
    // The sessionStorage flag prevents an infinite reload loop if the file is
    // genuinely missing from the CDN rather than just stale in the browser cache.
    const reloaded = sessionStorage.getItem("vite-preload-reloaded");
    if (!reloaded) {
        sessionStorage.setItem("vite-preload-reloaded", "1");
        window.location.reload();
        return;
    }

    // Reload didn't fix it — inform the user and log to Sentry.
    showAlert("alerts.newAppVersion");

    if (message.includes("Unable to preload CSS")) {
        // CSS-only failure: low severity, breadcrumb is sufficient for context.
        createSentryBreadcrumb(
            "error",
            `vite:preloadError (CSS): ${message}`,
            "warning",
        );
    } else {
        createSentryBreadcrumb(
            "error",
            `vite:preloadError (original): ${message}`,
            "error",
        );
        configSentryError(
            "main",
            "vite:preloadError",
            new Error("JS chunk failed to load after reload"),
        );
    }
});

app.use(pinia).use(router).use(vuetify).use(i18n).mount("#app");
